import { createContext, forwardRef, useContext, useEffect } from 'react'
import { createPortal } from 'react-dom'

import type { ComponentPropsWithoutRef, ReactNode } from 'react'

interface ModalContextValue {
  isOpen: boolean
  onClose: () => void
}

const ModalContext = createContext<ModalContextValue | null>(null)

function useModalContext() {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('Modal 하위 컴포넌트는 <Modal> 내부에서만 사용할 수 있습니다.')
  }
  return context
}

// --- Sub-components ---

const Backdrop = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({ onClick, ...props }, ref) => {
  const { onClose } = useModalContext()

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    onClose()
    onClick?.(e)
  }

  return <div ref={ref} onClick={handleClick} {...props} />
})
Backdrop.displayName = 'Modal.Backdrop'

const Content = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({ onClick, ...props }, ref) => {
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    const originalTouchAction = document.body.style.touchAction
    document.body.style.overflow = 'hidden'
    document.body.style.touchAction = 'none'
    return () => {
      document.body.style.overflow = originalOverflow
      document.body.style.touchAction = originalTouchAction
    }
  }, [])

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    onClick?.(e)
  }

  return <div ref={ref} role='dialog' aria-modal='true' onClick={handleClick} {...props} />
})
Content.displayName = 'Modal.Content'

const Title = forwardRef<HTMLParagraphElement, ComponentPropsWithoutRef<'p'>>((props, ref) => {
  return <p ref={ref} {...props} />
})
Title.displayName = 'Modal.Title'

const Description = forwardRef<HTMLParagraphElement, ComponentPropsWithoutRef<'p'>>((props, ref) => {
  return <p ref={ref} {...props} />
})
Description.displayName = 'Modal.Description'

const Actions = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>((props, ref) => {
  return <div ref={ref} {...props} />
})
Actions.displayName = 'Modal.Actions'

// --- Root ---

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
}

function ModalRoot({ isOpen, onClose, children }: ModalProps) {
  if (!isOpen) return null

  const container = document.getElementById('modal-root') ?? document.body

  return createPortal(
    <ModalContext.Provider value={{ isOpen, onClose }}>{children}</ModalContext.Provider>,
    container
  )
}

export const Modal = Object.assign(ModalRoot, {
  Backdrop,
  Content,
  Title,
  Description,
  Actions,
})
